import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  styled,
} from "@mui/material";
import { Handshake, X } from "@phosphor-icons/react";
import type { CollaborationNote } from "types/collaborationNote";

export type { CollaborationNote };

const StyledDialogContent = styled(DialogContent)({
  "&.MuiDialogContent-root": { padding: "24px" },
});

interface CollaborationNoteDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (note: string) => void;
  noteToEdit?: CollaborationNote;
  isSaving?: boolean;
}

/**
 * Renders an add or edit provider collaboration note modal.
 *
 * @param props - Component props.
 * @param props.open - Whether the modal is open.
 * @param props.onClose - Callback fired when the modal closes.
 * @param props.onSave - Callback fired when a note is submitted.
 * @param [props.noteToEdit] - When provided, pre-fills the form and switches to edit mode.
 */
const CollaborationNoteDialog: React.FC<CollaborationNoteDialogProps> = ({
  open,
  onClose,
  onSave,
  noteToEdit,
  isSaving,
}) => {
  const [noteValue, setNoteValue] = useState("");
  const isEditing = Boolean(noteToEdit);

  useEffect(() => {
    setNoteValue(open ? noteToEdit?.body ?? "" : "");
  }, [open, noteToEdit]);

  const handleSave = () => {
    const trimmedNote = noteValue.trim();

    if (!trimmedNote) {
      return;
    }

    onSave(trimmedNote);
    setNoteValue("");
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Box component="span" sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Handshake weight="duotone" />
          {isEditing ? "Edit Collaboration Note" : "Add Collaboration Note"}
        </Box>
        <IconButton aria-label="Close collaboration note dialog" onClick={onClose} size="small">
          <X />
        </IconButton>
      </DialogTitle>

      <StyledDialogContent>
        <TextField
          label="Collaboration Note"
          aria-label="Collaboration note"
          data-testid="provider-collaboration-note-input"
          fullWidth
          multiline
          minRows={3}
          placeholder="Add a collaboration note"
          value={noteValue}
          onChange={(event) => setNoteValue(event.target.value)}
        />
      </StyledDialogContent>

      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={!noteValue.trim() || isSaving}
          data-testid="provider-collaboration-note-save"
        >
          {isEditing ? "Save Changes" : "Add Collaboration Note"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CollaborationNoteDialog;
